import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { startupAPI, fileAPI, adminAPI, pitchEventAPI } from '../lib/api';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import FileUpload from '../components/FileUpload';
import FileShareModal from '../components/FileShareModal';
import AuditLogTable from '../components/AuditLogTable';
import { getApiUrl } from '../config/api.config';

const StartupDashboard = () => {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
	const [startup, setStartup] = useState(null);
	const [startupProfile, setStartupProfile] = useState(null);
	const [files, setFiles] = useState([]);
	const [investors, setInvestors] = useState([]);
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [showShareModal, setShowShareModal] = useState(false);
	const [showProfileMenu, setShowProfileMenu] = useState(false);
	const [sharingFile, setSharingFile] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [fileToShare, setFileToShare] = useState(null);
	const [shareVisibility, setShareVisibility] = useState('PRIVATE');
	const [uploadProgress, setUploadProgress] = useState(0);
	const [searchTerm, setSearchTerm] = useState('');
	const [dragActive, setDragActive] = useState(false);
	const [editData, setEditData] = useState({});
	const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
	const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
	const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });

	useEffect(() => {
		if (user) {
			loadStartupData();
		}
	}, [user]);

	// Update URL when tab changes
	useEffect(() => {
		setSearchParams({ tab: activeTab });
	}, [activeTab, setSearchParams]);

	// Sync activeTab when URL query param changes (e.g., browser back/forward)
	useEffect(() => {
		const tabFromUrl = searchParams.get('tab');
		if (tabFromUrl && ['overview', 'investors', 'events', 'files'].includes(tabFromUrl)) {
			setActiveTab(tabFromUrl);
		}
	}, [searchParams]);

	const loadStartupData = async () => {
		setLoading(true);
		try {
			// Load user/startup details - use startup endpoint instead of admin
			const userResponse = await fetch(getApiUrl(`users/me`), {
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});

			if (!userResponse.ok) {
				throw new Error('Failed to load user data');
			}

			const userData = await userResponse.json();
			setStartup(userData);

			// Load startup profile if available
			try {
				const profileData = await adminAPI.getStartupProfile(user.id);
				setStartupProfile(profileData);
			} catch (err) {
				console.log('Startup profile not found');
			}

			// Load files
			const filesData = await fileAPI.getFilesByModule('STARTUP', user.id);
			setFiles(filesData.map(file => ({ ...file, tags: file.tags || [] })));

			// Load investors
			try {
				const investorsResponse = await fetch(getApiUrl(`investments/startup/${user.id}/investors`), {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});
				if (investorsResponse.ok) {
					const investorsData = await investorsResponse.json();
					setInvestors(investorsData || []);
				}
			} catch (err) {
				console.error('Failed to load investors:', err);
			}

			// Load events
			try {
				const eventsData = await pitchEventAPI.getByStartup(user.id);
				setEvents(eventsData || []);
			} catch (err) {
				console.error('Failed to load events:', err);
			}
		} catch (err) {
			console.error('Failed to load startup data:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleFileUpload = async (file) => {
		if (!file) return;

		setUploading(true);
		setUploadProgress(0);
		try {
			await fileAPI.uploadFileWithProgress(file, (progress) => {
				setUploadProgress(progress);
			});
			await loadStartupData();
			setShowUploadModal(false);
			setSelectedFile(null);
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: err.response?.data?.error || 'Failed to upload file', type: 'error' });
		} finally {
			setUploading(false);
			setUploadProgress(0);
		}
	};

	const handleShare = (file) => {
		setSharingFile(file);
		setShowShareModal(true);
	};

	const confirmShare = async () => {
		try {
			await fileAPI.updateFile(fileToShare.id, { visibility: shareVisibility });
			await loadStartupData();
			setShowShareModal(false);
			setAlert({ isOpen: true, title: 'Success', message: 'File visibility updated successfully', type: 'success' });
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to update visibility', type: 'error' });
		}
	};

	const handleUpdateProfile = async () => {
		try {
			const response = await fetch(getApiUrl(`users/me`), {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${localStorage.getItem('token')}`
				},
				body: JSON.stringify(editData)
			});

			if (response.ok) {
				setAlert({ isOpen: true, title: 'Success', message: 'Profile updated successfully!', type: 'success' });
				setShowEditModal(false);
				loadStartupData();
			} else {
				const errorData = await response.json();
				setAlert({ isOpen: true, title: 'Error', message: errorData.message || 'Failed to update profile', type: 'error' });
			}
		} catch (err) {
			console.error('Failed to update profile:', err);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to update profile', type: 'error' });
		}
	};

	const handleChangePassword = async () => {
		if (passwordData.newPassword !== passwordData.confirmPassword) {
			setAlert({ isOpen: true, title: 'Error', message: 'New passwords do not match', type: 'error' });
			return;
		}

		try {
			const response = await fetch(getApiUrl(`admin/users/${user.id}`), {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${localStorage.getItem('token')}`
				},
				body: JSON.stringify({ password: passwordData.newPassword })
			});

			if (response.ok) {
				setAlert({ isOpen: true, title: 'Success', message: 'Password changed successfully!', type: 'success' });
				setShowPasswordModal(false);
				setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
			} else {
				setAlert({ isOpen: true, title: 'Error', message: 'Failed to change password', type: 'error' });
			}
		} catch (err) {
			console.error('Failed to change password:', err);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to change password', type: 'error' });
		}
	};

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			setSelectedFile(e.dataTransfer.files[0]);
		}
	};

	const previewFile = async (file) => {
		try {
			const response = await fileAPI.getFilePreview(file.id);
			window.open(response.previewUrl, '_blank');
		} catch (err) {
			alert('Failed to preview file');
		}
	};

	const downloadFile = async (file) => {
		try {
			const response = await fileAPI.getFileDownload(file.id);
			// Create blob from response data
			const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
			// Create object URL
			const url = window.URL.createObjectURL(blob);
			// Create download link
			const link = document.createElement('a');
			link.href = url;
			link.download = file.originalName || file.name || 'download';
			document.body.appendChild(link);
			link.click();
			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error('Failed to download file:', err);
			alert('Failed to download file');
		}
	};

	const deleteFile = (fileId) => {
		setConfirm({
			isOpen: true,
			title: 'Delete File',
			message: 'Are you sure you want to delete this file?',
			type: 'danger',
			onConfirm: async () => {
				await performDeleteFile(fileId);
			}
		});
	};

	const performDeleteFile = async (fileId) => {
		try {
			await fileAPI.deleteFile(fileId);
			await loadStartupData();
			setAlert({ isOpen: true, title: 'Success', message: 'File deleted successfully', type: 'success' });
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to delete file', type: 'error' });
		}
	};

	const handleLogout = () => {
		logout();
		navigate('/login');
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleDateString();
	};

	const formatDateTime = (dateString) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleString();
	};

	const filteredFiles = files.filter(file =>
		file.originalName.toLowerCase().includes(searchTerm.toLowerCase())
	);

	return (
		<div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50">
			{/* Header */}
			<header className="bg-white shadow-sm border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex justify-between items-center">
						<div className="flex items-center space-x-4">
							<div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg">
								<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
								</svg>
							</div>
							<div>
								<h1 className="text-2xl font-bold text-gray-900">{startup?.organizationName || startup?.name || 'Startup Dashboard'}</h1>
								<p className="text-sm text-gray-500">{startup?.department || 'Startup'}</p>
							</div>
						</div>
						<div className="flex items-center gap-4">
							<div className="text-right">
								<p className="text-sm font-medium text-gray-900">{user?.name}</p>
								<p className="text-xs text-gray-500">Startup</p>
							</div>
							<div className="relative">
								<button
									onClick={() => setShowProfileMenu(!showProfileMenu)}
									className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center hover:shadow-lg transition"
								>
									<span className="text-white text-sm font-semibold">
										{user?.name?.charAt(0)?.toUpperCase()}
									</span>
								</button>
								{showProfileMenu && (
									<div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
										<button
											onClick={() => {
												setShowPasswordModal(true);
												setShowProfileMenu(false);
											}}
											className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
											</svg>
											<span>Change Password</span>
										</button>
										<button
											onClick={() => {
												navigate(`/${user?.tenantIdentifier || 'default'}/my-subscription`);
												setShowProfileMenu(false);
											}}
											className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
											</svg>
											<span>Subscription</span>
										</button>
										<button
											onClick={() => {
												navigate(`/${user?.tenantIdentifier || 'default'}/billing`);
												setShowProfileMenu(false);
											}}
											className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
											</svg>
											<span>Billing</span>
										</button>
										<div className="border-t border-gray-200 my-1"></div>
										<button
											onClick={() => {
												handleLogout();
												setShowProfileMenu(false);
											}}
											className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
											</svg>
											<span>Logout</span>
										</button>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</header>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Tabs */}
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
					<div className="border-b border-gray-200">
						<nav className="flex -mb-px">
							{['overview', 'investors', 'events', 'files', 'audit'].map((tab) => (
								<button
									key={tab}
									onClick={() => {
										setActiveTab(tab);
										setSearchParams({ tab });
									}}
									className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab
										? 'border-blue-600 text-blue-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
								>
									{tab.charAt(0).toUpperCase() + tab.slice(1)}
								</button>
							))}
						</nav>
					</div>
				</div>

				{loading && activeTab === 'overview' ? (
					<div className="text-center py-16">
						<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
						</div>
						<p className="text-lg font-medium text-gray-900">Loading...</p>
					</div>
				) : (
					<>
						{/* Overview Tab */}
						{activeTab === 'overview' && (
							<div className="space-y-6">
								{/* Startup Details Card */}
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
									<div className="flex justify-between items-start mb-6">
										<h2 className="text-xl font-semibold text-gray-900">Startup Details</h2>
										<button
											onClick={() => {
												setEditData({
													name: startup?.name || '',
													email: startup?.email || '',
													phoneNumber: startup?.phoneNumber || '',
													organizationName: startup?.organizationName || '',
													department: startup?.department || '',
													jobTitle: startup?.jobTitle || ''
												});
												setShowEditModal(true);
											}}
											className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
										>
											Edit Profile
										</button>
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<div>
											<label className="text-sm font-medium text-gray-500">Name</label>
											<p className="mt-1 text-sm text-gray-900">{startup?.name || 'N/A'}</p>
										</div>
										<div>
											<label className="text-sm font-medium text-gray-500">Email</label>
											<p className="mt-1 text-sm text-gray-900">{startup?.email || 'N/A'}</p>
										</div>
										<div>
											<label className="text-sm font-medium text-gray-500">Phone</label>
											<p className="mt-1 text-sm text-gray-900">{startup?.phoneNumber || 'N/A'}</p>
										</div>
										<div>
											<label className="text-sm font-medium text-gray-500">Company Name</label>
											<p className="mt-1 text-sm text-gray-900">{startup?.organizationName || 'N/A'}</p>
										</div>
										<div>
											<label className="text-sm font-medium text-gray-500">Industry</label>
											<p className="mt-1 text-sm text-gray-900">{startup?.department || 'N/A'}</p>
										</div>
										<div>
											<label className="text-sm font-medium text-gray-500">Stage</label>
											<p className="mt-1 text-sm text-gray-900">{startup?.jobTitle || 'N/A'}</p>
										</div>
									</div>


								</div>
							</div>
						)}

						{/* Investors Tab */}
						{activeTab === 'investors' && (
							<div className="bg-white rounded-xl shadow-sm border border-gray-200">
								<div className="px-6 py-4 border-b border-gray-200">
									<h2 className="text-xl font-semibold text-gray-900">Related Investors</h2>
									<p className="text-sm text-gray-500 mt-1">Investors who have invested in your startup</p>
								</div>
								<div className="p-6">
									{investors.length === 0 ? (
										<div className="text-center py-12">
											<svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
											</svg>
											<h3 className="mt-2 text-sm font-medium text-gray-900">No investors yet</h3>
											<p className="mt-1 text-sm text-gray-500">Investors will appear here once they invest in your startup.</p>
										</div>
									) : (
										<div className="space-y-3">
											{investors.map((investor) => (
												<div key={investor.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition">
													<div className="flex items-center space-x-3">
														<div className="h-10 w-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center">
															<span className="text-white font-medium">
																{(investor.name || investor.organizationName)?.charAt(0)?.toUpperCase()}
															</span>
														</div>
														<div>
															<p className="text-sm font-medium text-gray-900">{investor.name || investor.organizationName}</p>
															<p className="text-xs text-gray-500">{investor.email}</p>
														</div>
													</div>
													<div className="text-right">
														<span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
															Active
														</span>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						)}

						{/* Events Tab */}
						{activeTab === 'events' && (
							<div className="bg-white rounded-xl shadow-sm border border-gray-200">
								<div className="px-6 py-4 border-b border-gray-200">
									<h2 className="text-xl font-semibold text-gray-900">Related Events</h2>
									<p className="text-sm text-gray-500 mt-1">Pitch events you are participating in</p>
								</div>
								<div className="p-6">
									{events.length === 0 ? (
										<div className="text-center py-12">
											<svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
											</svg>
											<h3 className="mt-2 text-sm font-medium text-gray-900">No events yet</h3>
											<p className="mt-1 text-sm text-gray-500">Events you participate in will appear here.</p>
										</div>
									) : (
										<div className="space-y-3">
											{events.map((event) => (
												<div key={event.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition">
													<div className="flex items-center space-x-3">
														<div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
															<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
															</svg>
														</div>
														<div>
															<p className="text-sm font-medium text-gray-900">{event.eventName}</p>
															<p className="text-xs text-gray-500">
																{formatDateTime(event.eventDate)} • {event.location || 'Location TBD'}
															</p>
														</div>
													</div>
													<div className="text-right">
														<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${event.status === 'UPCOMING' ? 'bg-blue-100 text-blue-800' :
															event.status === 'ONGOING' ? 'bg-green-100 text-green-800' :
																event.status === 'COMPLETED' ? 'bg-gray-100 text-gray-800' :
																	'bg-red-100 text-red-800'
															}`}>
															{event.status}
														</span>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						)}

						{/* Files Tab */}
						{activeTab === 'files' && (
							<div className="space-y-6">
								{/* Action Bar */}
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-6">
									<div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
										<div className="flex items-center space-x-4 w-full sm:w-auto">
											<div className="relative flex-1 sm:flex-initial">
												<input
													type="text"
													placeholder="Search files..."
													value={searchTerm}
													onChange={(e) => setSearchTerm(e.target.value)}
													className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
												/>
												<svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
												</svg>
											</div>
										</div>
										<button
											onClick={() => setShowUploadModal(true)}
											className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition duration-200 flex items-center justify-center space-x-2"
										>
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
											</svg>
											<span>Upload New File</span>
										</button>
									</div>
								</div>

								{/* Files Grid */}
								<div className="bg-white rounded-xl shadow-sm border border-gray-200">
									<div className="px-6 py-4 border-b border-gray-200">
										<h2 className="text-xl font-semibold text-gray-900">Files</h2>
									</div>

									<div className="p-6">
										{filteredFiles.length === 0 ? (
											<div className="text-center py-16">
												<svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
												</svg>
												<p className="text-lg font-medium text-gray-900 mb-2">
													{searchTerm ? 'No files found' : 'No documents yet'}
												</p>
												<p className="text-sm text-gray-500 mb-6">
													{searchTerm ? 'Try adjusting your search terms' : 'Upload your first document to get started'}
												</p>
												{!searchTerm && (
													<button
														onClick={() => setShowUploadModal(true)}
														className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition duration-200"
													>
														Upload First Document
													</button>
												)}
											</div>
										) : (
											<div className="overflow-x-auto">
												<table className="min-w-full divide-y divide-gray-200">
													<thead className="bg-gray-50">
														<tr>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Name
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Category
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Size
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Date
															</th>
															<th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
																Actions
															</th>
														</tr>
													</thead>
													<tbody className="bg-white divide-y divide-gray-200">
														{filteredFiles.map((file) => (
															<tr key={file.id} className="hover:bg-gray-50 transition-colors">
																<td className="px-6 py-4 whitespace-nowrap">
																	<div className="flex items-center">
																		<div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
																			<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
																			</svg>
																		</div>
																		<div className="ml-4">
																			<div className="text-sm font-medium text-gray-900 group relative">
																				{file.originalName}
																				{/* Description tooltip */}
																				{file.description && (
																					<span className="absolute left-0 -bottom-1 transform translate-y-full w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
																						{file.description}
																					</span>
																				)}
																			</div>
																			{file.description && (
																				<div className="text-xs text-gray-500 truncate max-w-xs">{file.description}</div>
																			)}
																		</div>
																	</div>
																</td>
																<td className="px-6 py-4 whitespace-nowrap">
																	{file.fileCategory ? (
																		<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${file.fileCategory.includes('PITCH') || file.fileCategory.includes('PRESENTATION') || file.fileCategory.includes('DECK') ? 'bg-purple-100 text-purple-800' :
																			file.fileCategory.includes('FINANCIAL') || file.fileCategory.includes('VALUATION') || file.fileCategory.includes('CAP_TABLE') || file.fileCategory.includes('BALANCE') || file.fileCategory.includes('CASH_FLOW') || file.fileCategory.includes('P_L') ? 'bg-green-100 text-green-800' :
																				file.fileCategory.includes('LEGAL') || file.fileCategory.includes('AGREEMENT') || file.fileCategory.includes('SHA') || file.fileCategory.includes('TERM') || file.fileCategory.includes('NDA') || file.fileCategory.includes('PATENT') || file.fileCategory.includes('TRADEMARK') || file.fileCategory.includes('IP') ? 'bg-blue-100 text-blue-800' :
																					file.fileCategory.includes('BUSINESS') || file.fileCategory.includes('MARKET') || file.fileCategory.includes('COMPETITIVE') || file.fileCategory.includes('PRODUCT') || file.fileCategory.includes('TECHNICAL') ? 'bg-orange-100 text-orange-800' :
																						file.fileCategory.includes('COMPLIANCE') || file.fileCategory.includes('LICENSE') || file.fileCategory.includes('REGULATORY') || file.fileCategory.includes('TAX') ? 'bg-yellow-100 text-yellow-800' :
																							file.fileCategory.includes('OPERATIONAL') || file.fileCategory.includes('HR') || file.fileCategory.includes('POLICY') || file.fileCategory.includes('PROCESS') ? 'bg-indigo-100 text-indigo-800' :
																								file.fileCategory.includes('MARKETING') || file.fileCategory.includes('SALES') || file.fileCategory.includes('BRAND') ? 'bg-pink-100 text-pink-800' :
																									'bg-gray-100 text-gray-800'
																			}`}>
																			{file.fileCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
																		</span>
																	) : (
																		<span className="text-gray-400">-</span>
																	)}
																</td>
																<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																	{file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : '-'}
																</td>
																<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																	{formatDateTime(file.uploadedAt)}
																</td>
																<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
																	<div className="flex justify-end space-x-3">
																		<button
																			onClick={() => previewFile(file)}
																			className="text-blue-600 hover:text-blue-900 transition-colors"
																			title="Preview"
																		>
																			<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
																			</svg>
																		</button>
																		<button
																			onClick={() => downloadFile(file)}
																			className="text-green-600 hover:text-green-900 transition-colors"
																			title="Download"
																		>
																			<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
																			</svg>
																		</button>
																		<button
																			onClick={() => handleShare(file)}
																			className="text-indigo-600 hover:text-indigo-900 transition-colors"
																			title="Share / Visibility"
																		>
																			<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
																			</svg>
																		</button>
																		<button
																			onClick={() => deleteFile(file.id)}
																			className="text-red-600 hover:text-red-900 transition-colors"
																			title="Delete"
																		>
																			<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
																			</svg>
																		</button>
																	</div>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}
									</div>
								</div>
							</div>
						)}
						{/* Audit Log Tab */}
						{activeTab === 'audit' && (
							<AuditLogTable />
						)}
					</>
				)}
			</div>

			{/* Edit Profile Modal */}
			{showEditModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-xl max-w-2xl w-full p-6">
						<div className="flex justify-between items-center mb-6">
							<h3 className="text-xl font-semibold text-gray-900">Edit Profile</h3>
							<button
								onClick={() => setShowEditModal(false)}
								className="text-gray-400 hover:text-gray-600"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
								<input
									type="text"
									value={editData.name || ''}
									onChange={(e) => setEditData({ ...editData, name: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
								<input
									type="email"
									value={editData.email || ''}
									onChange={(e) => setEditData({ ...editData, email: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
								<input
									type="text"
									value={editData.phoneNumber || ''}
									onChange={(e) => setEditData({ ...editData, phoneNumber: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
								<input
									type="text"
									value={editData.organizationName || ''}
									onChange={(e) => setEditData({ ...editData, organizationName: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
								<input
									type="text"
									value={editData.department || ''}
									onChange={(e) => setEditData({ ...editData, department: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
								<input
									type="text"
									value={editData.jobTitle || ''}
									onChange={(e) => setEditData({ ...editData, jobTitle: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
						</div>
						<div className="flex justify-end space-x-3 mt-6">
							<button
								onClick={() => setShowEditModal(false)}
								className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
							>
								Cancel
							</button>
							<button
								onClick={handleUpdateProfile}
								className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
							>
								Save Changes
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Change Password Modal */}
			{showPasswordModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-xl max-w-md w-full p-6">
						<div className="flex justify-between items-center mb-6">
							<h3 className="text-xl font-semibold text-gray-900">Change Password</h3>
							<button
								onClick={() => setShowPasswordModal(false)}
								className="text-gray-400 hover:text-gray-600"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
								<input
									type="password"
									value={passwordData.currentPassword}
									onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
								<input
									type="password"
									value={passwordData.newPassword}
									onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
								<input
									type="password"
									value={passwordData.confirmPassword}
									onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
								/>
							</div>
						</div>
						<div className="flex justify-end space-x-3 mt-6">
							<button
								onClick={() => setShowPasswordModal(false)}
								className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
							>
								Cancel
							</button>
							<button
								onClick={handleChangePassword}
								className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
							>
								Change Password
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Share File Modal */}
			{showShareModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-xl max-w-md w-full p-6">
						<div className="flex justify-between items-center mb-6">
							<h3 className="text-xl font-semibold text-gray-900">Share File</h3>
							<button
								onClick={() => setShowShareModal(false)}
								className="text-gray-400 hover:text-gray-600"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
						<div className="space-y-4">
							<p className="text-sm text-gray-600">
								Update visibility settings for <span className="font-medium text-gray-900">{fileToShare?.originalName}</span>
							</p>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Visibility
								</label>
								<select
									value={shareVisibility}
									onChange={(e) => setShareVisibility(e.target.value)}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
								>
									<option value="PRIVATE">Private - Only you can view</option>
									<option value="PUBLIC">Public - All users in organization</option>
									<option value="NETWORK">Network - Associated investors/networks</option>
									<option value="EVENT">Event - Event participants</option>
								</select>
								<p className="mt-1 text-xs text-gray-500">
									{shareVisibility === 'PRIVATE' && 'Only you and admins can access this file.'}
									{shareVisibility === 'PUBLIC' && 'Visible to everyone in your organization.'}
									{shareVisibility === 'NETWORK' && 'Visible to investors in your network.'}
									{shareVisibility === 'EVENT' && 'Visible to participants in events you join.'}
								</p>
							</div>
						</div>
						<div className="flex justify-end space-x-3 mt-6">
							<button
								onClick={() => setShowShareModal(false)}
								className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
							>
								Cancel
							</button>
							<button
								onClick={confirmShare}
								className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
							>
								Update Visibility
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Upload Modal */}
			{showUploadModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6 border-b border-gray-200">
							<div className="flex justify-between items-center">
								<h3 className="text-xl font-semibold text-gray-900">Upload Document</h3>
								<button
									onClick={() => {
										setShowUploadModal(false);
										setSelectedFile(null);
									}}
									className="text-gray-400 hover:text-gray-600 transition-colors"
								>
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6">
							<FileUpload
								moduleType="STARTUP"
								moduleId={user?.id}
								onUploadSuccess={() => {
									loadStartupData();
									setShowUploadModal(false);
									setAlert({ isOpen: true, title: 'Success', message: 'File uploaded successfully', type: 'success' });
								}}
								onUploadError={(msg) => setAlert({ isOpen: true, title: 'Error', message: msg, type: 'error' })}
								defaultVisibility="PRIVATE"
							/>
						</div>
					</div>
				</div>
			)}

			{/* Alert Dialog */}
			<AlertDialog
				isOpen={alert.isOpen}
				onClose={() => setAlert({ ...alert, isOpen: false })}
				title={alert.title}
				message={alert.message}
				type={alert.type}
			/>

			{/* Confirm Dialog */}
			<ConfirmDialog
				isOpen={confirm.isOpen}
				onClose={() => setConfirm({ ...confirm, isOpen: false })}
				onConfirm={confirm.onConfirm || (() => { })}
				title={confirm.title}
				message={confirm.message}
				type={confirm.type}
			/>
			{/* File Share Modal */}
			{showShareModal && sharingFile && (
				<FileShareModal
					file={sharingFile}
					isOpen={showShareModal}
					onClose={() => {
						setShowShareModal(false);
						setSharingFile(null);
					}}
					onSuccess={() => {
						loadStartupData();
						setAlert({ isOpen: true, title: 'Success', message: 'File permissions updated successfully', type: 'success' });
					}}
				/>
			)}
		</div>
	);
};

export default StartupDashboard;
